import React, { useState, useEffect } from 'react';
import { eventsApi } from '../../api/eventsApi';
import { 
  UsersIcon, 
  ClockIcon, 
  DocumentTextIcon, 
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  ChartBarIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ShareIcon,
  TrophyIcon,
  SparklesIcon,
  FireIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

const EventReportDisplay = ({ eventId, eventUrl, onPublished }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [publishError, setPublishError] = useState('');
  const [publishedUrl, setPublishedUrl] = useState(eventUrl || null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Update publishedUrl when eventUrl prop changes
  useEffect(() => {
    if (eventUrl) {
      setPublishedUrl(eventUrl);
    }
  }, [eventUrl]);

  useEffect(() => {
    checkAndLoadReport();
  }, [eventId]);

  const handlePublishToWordPress = async () => {
    if (!eventId) return;
    
    setPublishing(true);
    setPublishError('');
    setPublishSuccess(false);
    
    try {
      const result = await eventsApi.publishToWordPress(eventId);
      setPublishSuccess(true);
      
      // Capture the published URL from response
      if (result.published_url) {
        setPublishedUrl(result.published_url);
      }
      
      // Notify parent to reload event data (so URL persists after page refresh)
      if (onPublished) {
        onPublished(result.published_url);
      }
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setPublishSuccess(false), 5000);
    } catch (err) {
      setPublishError(err.response?.data?.detail || err.message || 'Failed to publish to WordPress');
    } finally {
      setPublishing(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!eventId) return;
    
    setDownloadingPdf(true);
    try {
      await eventsApi.downloadReportPdf(eventId);
    } catch (err) {
      console.error('PDF download error:', err);
      setPublishError(err.response?.data?.detail || err.message || 'Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const checkAndLoadReport = async () => {
    if (!eventId) return;
    
    setLoading(true);
    setError('');
    
    try {
      // First check status
      const status = await eventsApi.getReportStatus(eventId);
      
      // If no report or not completed, don't try to load it
      if (!status || status.status === 'NO_REPORT') {
        setReport(null);
        setLoading(false);
        return;
      }
      
      if (status.status === 'PENDING' || status.status === 'PROCESSING') {
        setError('Report is being generated. Please wait...');
        setLoading(false);
        return;
      }
      
      if (status.status === 'FAILED') {
        setError(status.error_message || 'Report generation failed');
        setLoading(false);
        return;
      }
      
      // Only fetch report if COMPLETED
      if (status.status === 'COMPLETED') {
        const response = await eventsApi.getReport(eventId);
        // API returns { data: {...} }, extract the actual report data
        const reportData = response.data || response;
        setReport(reportData);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setReport(null);
      } else {
        setError(err.response?.data?.detail || 'Failed to load report');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-center gap-3 text-gray-500">
          <ArrowPathIcon className="h-5 w-5 animate-spin" />
          <span>Loading report...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 text-amber-600">
          <ExclamationCircleIcon className="h-5 w-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  const stats = report.globalStatistics || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-2xl font-bold">{report.eventName}</h2>
            <p className="mt-1 text-indigo-100">{report.reportInfo}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="bg-white/20 px-4 py-2 rounded-lg">
              <span className="text-lg font-semibold">{report.totalSessionReports}</span>
              <span className="ml-1 text-indigo-100">sessions</span>
            </div>
            {/* Download PDF button */}
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Download report as PDF"
            >
              {downloadingPdf ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  <span className="text-sm font-medium">Generating...</span>
                </>
              ) : (
                <>
                  <ArrowDownTrayIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Download PDF</span>
                </>
              )}
            </button>
            {/* Publish to WordPress button */}
            <button
              onClick={handlePublishToWordPress}
              disabled={publishing}
              className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Publish to WordPress"
            >
              {publishing ? (
                <>
                  <ArrowPathIcon className="h-5 w-5 animate-spin" />
                  <span className="text-sm font-medium">Publishing...</span>
                </>
              ) : publishSuccess ? (
                <>
                  <CheckCircleIcon className="h-5 w-5 text-green-300" />
                  <span className="text-sm font-medium">Published!</span>
                </>
              ) : (
                <>
                  <GlobeAltIcon className="h-5 w-5" />
                  <span className="text-sm font-medium">Publish to WordPress</span>
                </>
              )}
            </button>
          </div>
        </div>
        {/* Publish error/success messages */}
        {publishError && (
          <div className="mt-4 p-3 bg-red-500/20 border border-red-400/30 rounded-lg flex items-center gap-2">
            <ExclamationCircleIcon className="h-5 w-5 text-red-300" />
            <span className="text-sm text-red-100">{publishError}</span>
          </div>
        )}
        {publishSuccess && (
          <div className="mt-4 p-3 bg-green-500/20 border border-green-400/30 rounded-lg flex items-center gap-2">
            <CheckCircleIcon className="h-5 w-5 text-green-300" />
            <span className="text-sm text-green-100">Event report successfully published to WordPress!</span>
          </div>
        )}
        {/* Published URL Display */}
        {publishedUrl && (
          <div className="mt-4 p-3 bg-white/10 border border-white/20 rounded-lg">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-white/70 uppercase mb-1">Published on WordPress</div>
                <a
                  href={publishedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/90 hover:text-white hover:underline truncate block"
                  title={publishedUrl}
                >
                  {publishedUrl}
                </a>
              </div>
              <a
                href={publishedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-white/20 border border-white/30 rounded-lg hover:bg-white/30 transition-colors"
                title="Open in new tab"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                Open
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Global Statistics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard 
          icon={UsersIcon}
          label="Unique Speakers"
          value={stats.uniqueSpeakers}
          color="blue"
        />
        <StatCard 
          icon={ChatBubbleLeftRightIcon}
          label="Total Speeches"
          value={stats.totalSpeeches?.toLocaleString()}
          color="green"
        />
        <StatCard 
          icon={ClockIcon}
          label="Total Time"
          value={stats.totalTimeDescription || formatTime(stats.totalTime)}
          color="purple"
        />
        <StatCard 
          icon={DocumentTextIcon}
          label="Total Words"
          value={stats.totalLength?.toLocaleString()}
          color="orange"
        />
      </div>

      {/* Analysis Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-green-50 rounded-xl p-5 border border-green-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <LightBulbIcon className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-700">{stats.agreedPoints || 0}</p>
              <p className="text-sm text-green-600">Agreed Points</p>
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 rounded-xl p-5 border border-red-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <FireIcon className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-700">{stats.pointsOfDifference || 0}</p>
              <p className="text-sm text-red-600">Points of Difference</p>
            </div>
          </div>
        </div>
        
        <div className="bg-amber-50 rounded-xl p-5 border border-amber-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <SparklesIcon className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-700">{stats.thoughtProvokingComments || 0}</p>
              <p className="text-sm text-amber-600">Thought-Provoking Comments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Prominent Sessions */}
      {report.prominentSessions && Object.keys(report.prominentSessions).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrophyIcon className="h-5 w-5 text-yellow-500" />
            Notable Sessions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {report.prominentSessions.longestSession && (
              <SessionHighlight 
                label="Longest Session"
                session={report.prominentSessions.longestSession}
              />
            )}
            {report.prominentSessions.mostSpeakersSession && (
              <SessionHighlight 
                label="Most Speakers"
                session={report.prominentSessions.mostSpeakersSession}
                suffix=" speakers"
              />
            )}
            {report.prominentSessions.mostWordsSession && (
              <SessionHighlight 
                label="Most Words"
                session={report.prominentSessions.mostWordsSession}
                suffix=" words"
                formatValue
              />
            )}
            {report.prominentSessions.mostArgumentsSession && (
              <SessionHighlight 
                label="Most Arguments"
                session={report.prominentSessions.mostArgumentsSession}
                suffix=" arguments"
              />
            )}
          </div>
        </div>
      )}

      {/* Fastest Speakers */}
      {report.fastestSpeakers && report.fastestSpeakers.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-blue-500" />
            Fastest Speakers
          </h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="pb-3 pr-4">#</th>
                  <th className="pb-3 pr-4">Speaker</th>
                  <th className="pb-3">Speed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {report.fastestSpeakers.slice(0, 10).map((speaker) => (
                  <tr key={speaker.position} className="text-sm">
                    <td className="py-2 pr-4">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                        speaker.position <= 3 ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {speaker.position}
                      </span>
                    </td>
                    <td className="py-2 pr-4 font-medium text-gray-900">{speaker.speakerName}</td>
                    <td className="py-2 text-gray-600">{speaker.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Top Keywords/Prefixes */}
      {report.topPrefixes && report.topPrefixes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Keywords</h3>
          <div className="flex flex-wrap gap-3">
            {report.topPrefixes.map((prefix, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg px-4 py-2"
              >
                <span className="font-medium text-blue-800">{prefix.prefixName}</span>
                <span className="ml-2 text-sm text-gray-500">({prefix.eventValue} mentions)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Q&A Section */}
      {report.questions && report.questions.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Q&A Analysis</h3>
          <div className="space-y-3">
            {report.questions.map((qa, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedQuestion(expandedQuestion === idx ? null : idx)}
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900">{qa.question}</span>
                  {expandedQuestion === idx ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
                {expandedQuestion === idx && (
                  <div 
                    className="px-4 pb-4 prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: qa.answer }}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Embeds (Chatbot & Graph) */}
      <div className="space-y-6">
        {/* Chatbot */}
        {report.chatbot && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden w-full">
            <div className="p-4 border-b border-gray-200 flex items-center gap-2">
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-gray-900">{report.chatbot_name || 'Event Chatbot'}</h3>
            </div>
            {report.chatbot_disclaimer && (
              <div className="p-4 bg-amber-50 border-b border-amber-200">
                <p className="text-sm text-amber-800">{report.chatbot_disclaimer}</p>
              </div>
            )}
            <div className="w-full h-[600px]">
              <iframe 
                src={report.chatbot}
                className="w-full h-full border-0"
                title={report.chatbot_name || "Event Chatbot"}
                allow="microphone"
              />
            </div>
          </div>
        )}

        {/* Knowledge Graph */}
        {report.graph_path && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden w-full">
            <div className="p-4 border-b border-gray-200 flex items-center gap-2">
              <ShareIcon className="h-5 w-5 text-purple-600" />
              <h3 className="font-semibold text-gray-900">Knowledge Graph</h3>
            </div>
            <div className="w-full h-[600px]">
              <iframe 
                src={report.graph_path.startsWith('http') 
                  ? report.graph_path 
                  : `${process.env.REACT_APP_API_BASE_URL}${report.graph_path}`}
                className="w-full h-full border-0"
                title="Knowledge Graph"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    green: 'bg-green-50 text-green-600 border-green-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    orange: 'bg-orange-50 text-orange-600 border-orange-100',
  };

  return (
    <div className={`rounded-xl p-4 border ${colorClasses[color]}`}>
      <div className="flex items-center gap-3">
        <Icon className="h-8 w-8 opacity-80" />
        <div>
          <p className="text-2xl font-bold">{value || 0}</p>
          <p className="text-xs opacity-80">{label}</p>
        </div>
      </div>
    </div>
  );
};

// Session Highlight Component
const SessionHighlight = ({ label, session, suffix = '', formatValue = false }) => {
  const value = formatValue && typeof session.value === 'number' 
    ? session.value.toLocaleString() 
    : session.value;

  return (
    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <p className="mt-1 font-semibold text-gray-900">{session.sessionName}</p>
      <p className="text-sm text-gray-600">
        {value}{typeof session.value === 'number' && suffix}
      </p>
    </div>
  );
};

// Helper function to format time
const formatTime = (seconds) => {
  if (!seconds) return '0 seconds';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);
  
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 && hours === 0) parts.push(`${secs}s`);
  
  return parts.join(' ') || '0 seconds';
};

export default EventReportDisplay;

